import Axios from 'axios';
import decodeJwt from 'jwt-decode';
import {
  ENV,
  LOCAL_STORAGE_USER_CHAT_FIELD,
  LOCAL_STORAGE_USER_INFO_FIELD,
  PLATFORM_TAKE_RATE_INSTANT_BOOK,
  PLATFORM_TAKE_RATE_STANDARD,
  SUPPORT_EMAIL,
} from '../config';
import { IInstagramMedia } from '../types/elfsight/instagram';
import { IAddress } from '../types/location/address';
import { fileToDataURI } from './fileUtils';
import { uploadFileToCloudflareUrl } from './uploadUtils';

export function getUserDataFromStorage(): any {
  const item = localStorage.getItem(LOCAL_STORAGE_USER_INFO_FIELD) || '{}';
  if (item) {
    return JSON.parse(item) || '';
  }
  return {};
}

export function setUserDataToStorage(item: any = {}): any {
  let currentItem: any = localStorage.getItem(LOCAL_STORAGE_USER_INFO_FIELD);
  currentItem = currentItem ? JSON.parse(currentItem) : {};
  const newItem = { ...currentItem, ...item };
  return localStorage.setItem(
    LOCAL_STORAGE_USER_INFO_FIELD,
    JSON.stringify(newItem)
  );
}

export function clearUserDataFromStorage(): any {
  localStorage.removeItem(LOCAL_STORAGE_USER_CHAT_FIELD);
  return localStorage.setItem(LOCAL_STORAGE_USER_INFO_FIELD, '{}');
}

export function isTokenExpired(token: string) {
  if (token) {
    try {
      const decoded: any = decodeJwt(token);
      const now = Date.now().valueOf() / 1000;
      if (!(typeof decoded.exp !== 'undefined' && decoded.exp < now)) {
        // not expired
        return false;
      }
    } catch (error) {
      return true;
    }
  }
  return true;
}

export function getAuthHeader(
  header = {}
): { headers: { Authorization?: string; [other: string]: any } } | {} {
  const user = getUserDataFromStorage();

  if (user && user.token) {
    return {
      headers: {
        ...{ 'Content-Type': 'application/json' },
        ...header,
        Authorization: 'Bearer ' + user.token,
      },
    };
  } else {
    return { headers: { ...header } };
  }
}

export function isLoggedIn() {
  const token = getUserDataFromStorage().token;
  if (!isTokenExpired(token)) {
    return true;
  } else {
    return false;
  }
}

export function isEmailValid(email: string) {
  // eslint-disable-next-line
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}

export function getReadableDate(dateString) {
  return new Date(dateString).toDateString();
}

function getDeviceType() {
  var check = false;
  (function (a) {
    if (
      // eslint-disable-next-line
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
        a
      ) ||
      // eslint-disable-next-line
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
        a.substr(0, 4)
      )
    )
      check = true;
  })(navigator.userAgent || navigator.vendor || window['opera']);
  return check ? 'mobile' : 'desktop';
}

/**
 * True when the browser reports a phone / mobile UA (not merely a narrow viewport).
 * Includes iPadOS Safari, which often identifies as Macintosh + touch.
 */
export function isLikelyMobileDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  if (getDeviceType() === 'mobile') return true;
  if (
    /Macintosh/.test(navigator.userAgent) &&
    navigator.maxTouchPoints > 1
  ) {
    return true;
  }
  return false;
}

export function getOperatingSystem() {
  if (getDeviceType() === 'desktop') {
    let OSName = 'Unknown OS';
    if (navigator.appVersion.indexOf('Win') !== -1) OSName = 'Windows';
    if (navigator.appVersion.indexOf('Mac') !== -1) OSName = 'MacOS';
    if (navigator.appVersion.indexOf('X11') !== -1) OSName = 'UNIX';
    if (navigator.appVersion.indexOf('Linux') !== -1) OSName = 'Linux';
    return OSName;
  } else {
    let userAgent = navigator.userAgent || navigator.vendor || window['opera'];
    if (
      userAgent.match(/iPad/i) ||
      userAgent.match(/iPhone/i) ||
      userAgent.match(/iPod/i)
    ) {
      return 'iOS';
    } else if (userAgent.match(/Android/i)) {
      return 'Android';
    } else {
      return 'unknown';
    }
  }
}

export function formatSmsLinkForDevice(num: string, body: string): string {
  const OS = getOperatingSystem();
  if (OS === 'MacOS' || OS === 'iOS') {
    return `sms://${num}/&body=${encodeURI(body)}`;
  }
  return `sms://${num}/?body=${encodeURI(body)}`;
}

export function convertTime24hto12h(time) {
  // Check correct time format and split into components
  time = time.toString().match(/^([01]\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/) || [
    time,
  ];

  if (time.length > 1) {
    // If time format correct
    time = time.slice(1); // Remove full string match value
    time[5] = +time[0] < 12 ? 'AM' : 'PM'; // Set AM/PM
    time[0] = +time[0] % 12 || 12; // Adjust hours
  }
  return time.join(''); // return adjusted time or original string
}

export function updateItemInArray<T>(
  array: T[],
  itemToUpdate: T,
  fieldToMatch: string = 'id'
): T[] {
  return array.map((arr) =>
    arr[fieldToMatch] === itemToUpdate[fieldToMatch]
      ? { ...arr, ...itemToUpdate }
      : arr
  );
}

export function getServicePricing(cost: number, takeRate) {
  const takeRateInPercentage = takeRate / 100;
  const price = Math.ceil(cost / (1 - takeRateInPercentage));
  return { price: (!isNaN(price) && price) || '', cost };
}

/**
 * Effective take % for Instant Book vs standard bookings.
 * Mirrors flayr-api/utils/resolveTakeRate.js (platform takeRates, then category).
 */
export function resolveTakeRate(
  providerCategoryTakeRate: number | undefined,
  isInstantBook: boolean
): number {
  const category = providerCategoryTakeRate ?? 22;
  if (isInstantBook && PLATFORM_TAKE_RATE_INSTANT_BOOK != null) {
    return PLATFORM_TAKE_RATE_INSTANT_BOOK;
  }
  if (!isInstantBook && PLATFORM_TAKE_RATE_STANDARD != null) {
    return PLATFORM_TAKE_RATE_STANDARD;
  }
  return category;
}

/** Customer list price range: min/max across IB vs standard take for the same "you get" amount. */
export function formatCustomerPaysRange(
  cost: number,
  categoryTakeRate: number | undefined,
  currencySymbol: string
): string {
  const rIb = resolveTakeRate(categoryTakeRate, true);
  const rStd = resolveTakeRate(categoryTakeRate, false);
  const pIb = getServicePricing(cost, rIb).price;
  const pStd = getServicePricing(cost, rStd).price;
  if (pIb === '' || pStd === '' || pIb === undefined || pStd === undefined) {
    return '';
  }
  const n1 = Number(pIb);
  const n2 = Number(pStd);
  const lo = Math.min(n1, n2);
  const hi = Math.max(n1, n2);
  const sym = currencySymbol ?? '';
  if (lo === hi) {
    return `${sym}${lo}`;
  }
  return `${sym}${lo}–${sym}${hi}`;
}

// export async function getInstagramWebData(username) {
//   try {
//     const instagramRegExp = new RegExp(
//       /<script type="text\/javascript">window\._sharedData = (.*)<\/script>/
//     );
//     document.cookie =
//       'ig_cb=2; ig_did=06DE5BC0-C9AC-428F-B246-1B3D49FE8092; mid=X36ceAAEAAGlom7-deEYl_jEfSEg; fbm_124024574287414=base_domain=.instagram.com; csrftoken=c8S5bbHKQfSLJwpGVltbUpmehjeh24Sf; ds_user_id=6187163116; sessionid=6187163116%3AgUBKRuCgF0hize%3A9; shbid=13850; shbts=1612421985.5489707; rur=PRN; fbsr_124024574287414=bPo62I4W913zVOuf2aYJUdUf6rv7WFjozE7n3AWdEHc.eyJ1c2VyX2lkIjoiMTIyMzk3MzcwMCIsImNvZGUiOiJBUUFJbzNRNllSQXZfRFB4cHJvckZSYzVGbkZfUFo2WGMzV3RWejV4UVdqQm9hcUNhdGZmU3VBYzl1OXdQYmE5azViUjdnNmItSFF3MzVibVFYLVd2QUxGT0QxSk51Zkctd3JXdGNZM01PaTRmWHJsdUk1S01ROEpfZ3U5TXg3V2xJWldnaXZiZVRwQU9zMUY3MHhjeGVLb2huUHVLaC01cng0VlZfY1J1eUpUaFFVNnB6YURFSWM5eERlM29wWlBWNjhWa0lyeXhaNEt1T1ZHWWlSakdkdzVydkI1b1U4dEhvVXE5WXgzRVI0amwxenZGeTd5ZTl1ZnB4OUM2ZDVhMllZaEktUjFhSHEwSWNQNzRuMTAyYVI2aHp6UW8tNmtyNkxtd1hjbTdTZHBQLVA5WWtRME1ubWVWNThTandPUmVGOCIsIm9hdXRoX3Rva2VuIjoiRUFBQnd6TGl4bmpZQkFGSWU3WkJ0a1FFN3hqSkttc1pDM01jY2szcUl3aTdBWkIzbHYwSDZHNVZOZ3BBT3doeDZ4STdSNU55V08zQzlwWWhwcmFuYzFsbnZHN3BBN1FaQ1laQ3BObGtLajRUcTlrMTZFUzdNZHBDTVY4V2pBMXJLdm5BZHNiWU82Z2J4NmZMVG5xVTVYWEhNMlROWVd6UmxxekNEQ3BxQktoQmVTSUh2Q0xST3kiLCJhbGdvcml0aG0iOiJITUFDLVNIQTI1NiIsImlzc3VlZF9hdCI6MTYxMjQyMjMwNn0; fbsr_124024574287414=KXdJnoEubLiCiIQ4mva8Xtx-5SBQ8-_4Kpv4OS_oGvw.eyJ1c2VyX2lkIjoiMTIyMzk3MzcwMCIsImNvZGUiOiJBUUItVkEwcGRxVlhZa09xc1lDbWI2YjhYZU5kQnV1d1VUWkFmNS1jOG9NenJZdzFueG1fSTBoSFBDNnhvNi01VlpvNVNHSk1uS1FiZTBfLXhJYW0wTExPSndLZFBTY19ZNjNkWHJ0MFZ0ODZQWFFEeVBzVDdIdS12aHhiRVlyOHRLcXVpcDRrYnk5ZEZuVEVLWDFhd19JaVJybXp1TmpGZzdMbWFKVXJ3aXdhY0x0TGZ0cTJxbFlBcEtNY0lyRWdGVUpLekNTSVU0Si04dHQyRFJ6Um9iRlZ1bkZtd2M3RnlEemxKV01WV0dWYWhyT0U5cS1zLVVqNzhaaWt5Wm11VVctUkNWMzZZZmZVbmJxVzAtWmh5MTNwV0FOY1g1TktaZVJMaVVtR2doMnVzRmJEN1ktLWNoVXk0N3lWZDM2TlNXQSIsIm9hdXRoX3Rva2VuIjoiRUFBQnd6TGl4bmpZQkFGb1dZMEI5QzNBZVgzM2J1UEc1SVhHeXJmaXczdHBUSEhoSVhRTkd3YmU2cFpBQ0hIc3pUN2Z5RVl1aDBUM1cycmNOYWkySWM2UFBYbzB6ZzhDUzVFY1BRakhxVXJiZGJRUWFMRVFRYnZ6dUR4anhCMWNiNVpBY05ZTkhaQnoyUlZKclhsSGJVUTg5Q2JWZU9nNjdPWkFldEZhdGtMTkdrQ1lJVnM2dSIsImFsZ29yaXRobSI6IkhNQUMtU0hBMjU2IiwiaXNzdWVkX2F0IjoxNjEyNDI0Nzk3fQ';
//     const res = (
//       await Axios.get(`https://www.instagram.com/${username}/`, {
//         withCredentials: true,
//       })
//     ).data;
//     const jsonObject = res.match(instagramRegExp)[1].slice(0, -1);
//     const parsedData = JSON.parse(jsonObject);
//     const userData = parsedData?.entry_data?.ProfilePage?.[0]?.graphql?.user;
//     const images = userData?.edge_owner_to_timeline_media?.edges
//       ?.map((i) => i.node)
//       .filter((i) => i.__typename === 'GraphImage');
//     const user = {
//       bio: userData?.biography,
//       instaWebLink: userData?.external_url,
//       profileImg: userData?.profile_pic_url_hd,
//       instaName: userData?.full_name,
//       instaUsername: userData?.username,
//       instaPk: userData?.id,
//     };
//     return { user, images, success: true };
//   } catch (err) {
//     if (err?.response?.status === 404) return { success: false };
//   }
// }

export function getInstaProfileFromElfsignt(profile) {
  // https://api.instacloud.io/v1
  //query  /users/prajwal0078/profile/
}

export function getInstaImgFromElfsight(
  username,
  count = 16
): Promise<{ data: IInstagramMedia }> {
  const path = `/users/${username}/media/recent`;
  return Axios.get('https://api.instacloud.io/v1', {
    params: { path, count },
  });
}

export function addOrRemoveItemInArray(
  collection: any[],
  newSelection: any,
  field = '_id'
) {
  if (typeof newSelection === 'string') {
    if (collection.includes(newSelection)) {
      return [...collection.filter((item) => item !== newSelection)];
    } else {
      return [...collection, newSelection];
    }
  }
  const existing = collection.indexOf(newSelection) !== -1;
  if (existing) {
    return [
      ...collection.filter((item) => item[field] !== newSelection[field]),
    ];
  } else {
    return [...collection, newSelection];
  }
}

export function removeItemFromArray(
  collection: any[],
  value,
  field: string = 'id'
) {
  return collection.filter((item) => {
    if (typeof item === 'object') {
      return item[field] !== value;
    }
    return item !== value;
  });
}

export function removeItemsFromArray(
  collection: any[],
  values: any[],
  field: string = 'id'
) {
  return collection.filter((item) => {
    if (typeof item === 'object') {
      return !values.some((value) => item[field] === value[field]);
    }
    return !values.includes(item);
  });
}

export function generateId() {
  let a = new Uint32Array(3);
  window.crypto.getRandomValues(a);
  return (
    performance.now().toString(36) +
    Array.from(a)
      .map((A) => A.toString(36))
      .join('')
  ).replace(/\./g, '');
}

export async function uploadImg(file, _id, options: any = { convertToWebp: false }) {
  const user = getUserDataFromStorage();
  if (!user?.token) {
    throw new Error('You must be signed in to upload images.');
  }
  return uploadFileToCloudflareUrl(file, user.token, options);
}

// export async function uploadSingleImage(image: File, options) {
//   const data: any = new FormData();
//   const headers = {
//     'Content-Type': `multipart/form-data; boundary=${data._boundary}`,
//   };
//   data.append('images', image);
//   if (options.fileName) {
//     data.append('fileName', options.fileName);
//   }
//   if (options.id) {
//     data.append('id', options.id);
//   }
//   return Axios.post(`/v1/image/upload-single-image`, data, {
//     headers,
//   });
// }

export function sendEmail(
  emails: string | string[],
  subject: string,
  body: string
) {
  return Axios.post(
    'https://it0fjfpp3d.execute-api.ap-southeast-2.amazonaws.com/prod',
    {
      to: emails,
      fromEmail: 'team@flayr.io',
      fromName: 'Team Flayr',
      subject,
      text: ' ',
      html: body,
    }
  );
}

export function postEmail(
  to: { emails?: string[]; subject: string; body: string; email?: string },
  from: { email: string; name: string } = {
    email: 'team@flayr.io',
    name: 'Team Flayr',
  }
) {
  const { emails, subject, body, email: toEmail } = to;
  const { email, name } = from;

  return Axios.post(
    'https://it0fjfpp3d.execute-api.ap-southeast-2.amazonaws.com/prod',
    {
      to: emails || toEmail,
      fromEmail: email,
      fromName: name,
      subject,
      text: ' ',
      html: body,
    }
  );
}

export function isNumber(evt) {
  evt = evt ? evt : window.event;
  var charCode = evt.which ? evt.which : evt.keyCode;
  if (charCode > 31 && (charCode < 48 || charCode > 57)) {
    return false;
  }
  return true;
}

export function isAlfa(evt) {
  evt = evt ? evt : window.event;
  var charCode = evt.which ? evt.which : evt.keyCode;
  if (
    charCode > 31 &&
    (charCode < 65 || charCode > 90) &&
    (charCode < 97 || charCode > 122)
  ) {
    return false;
  }
  return true;
}

export function generateArrayFromNumber(number: number, value) {
  if (!number || number < 1 || !value) {
    return [];
  }
  return Array(number).fill(value);
}

export function getPercentageOf(number, percentage) {
  return (number && percentage && (percentage / 100) * number) || 0;
}

export function escapeRegex(string) {
  return string.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
}

export function appendToPathName(pathName: string, strToAppend: string) {
  const newPath = (pathName + '/' + strToAppend).replace(/(\/{2,})/g, '/');
  return newPath;
}

export function getDistanceInMeters(value, unit = 'mi') {
  return (unit.includes('mi') && value * 1609) || value * 1000;
}

export function getMailToLink(subject = '', email = SUPPORT_EMAIL) {
  return `mailto:${email}?subject=${subject}`;
}

export function debug(message) {
  if (ENV !== 'production') {
    if (typeof message === 'object') {
      message = JSON.stringify(message, null, 2);
    }
    console.log(`%c ${message}`, 'background: #222; color: #bada55');
  }
}

export function delay(duration = 3000) {
  return new Promise((resolve) => {
    setTimeout(() => {
      return resolve(duration);
    }, duration);
  });
}

export function swapKeyValue(obj) {
  const result = {};
  for (let key in obj) {
    const value = obj[key];
    result[value] = key;
  }
  return result;
}

export function replaceUrlParamsWithValue(url, values) {
  Object.keys(values).forEach((key) => {
    const value = values[key];
    url = url.replace(`:${key}`, value);
  });
  return url;
}

export function formatAddress(address?: IAddress, skip: string[] = []) {
  if (!address) {
    return '';
  }

  const fields = [
    'locality',
    'neighborhood',
    'place',
    'region',
    'country',
  ].filter((f) => !skip.includes(f));

  const locationArray = fields
    .map((field) => address?.[field])
    .filter((f) => f);
  return locationArray.join(', ');
}

export function parseQueryString(query) {
  const parsedQuery =
    (query &&
      JSON.parse(
        '{"' +
          decodeURI(
            query.replace('?', '').replace(/&/g, '","').replace(/=/g, '":"')
          ) +
          '"}'
      )) ||
    {};
  return parsedQuery;
}

export function addToSerchParams(currentSearch, itemsToAdd) {
  const parsedQuery = parseQueryString(currentSearch);
  const added = { ...parsedQuery, ...itemsToAdd };
  const result = Object.keys(added)
    .map((key) => `${key}=${added[key]}`)
    .join('&');

  return (result && `?${result}`) || '';
}

export {
  getPostLoginRedirectUrl,
  getDefaultDashboardPathForUser,
} from './getPostLoginRedirectUrl';
